import React, { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";

import LHModalPop from "../../../components/LHModalPop";
import { useTheme } from "../../../theme";
import { createApiClient } from "../../../api/apiClient";
import {
  ServiceNowIncidentDraft,
  ServiceNowIncidentPreview,
  ServiceNowIncidentValidationErrors,
} from "../../../types/servicenow";

type Props = {
  visible: boolean;
  ticketId?: string | number;
  existingIncidentNumber?: string;
  onClose: () => void;
  onProceed: (draft: ServiceNowIncidentDraft) => void | Promise<void>;
  isSubmitting?: boolean;
};

const MAX_SHORT_DESCRIPTION_LENGTH = 160;
const MAX_DESCRIPTION_LENGTH = 4000;

const toDraft = (preview: ServiceNowIncidentPreview, existingInc?: string): ServiceNowIncidentDraft => ({
  ...preview,
  existingIncidentNumber: existingInc || "",
});

const validateDraft = (draft: ServiceNowIncidentDraft): ServiceNowIncidentValidationErrors => {
  const errors: ServiceNowIncidentValidationErrors = {};

  if (!draft.shortDescription.trim()) {
    errors.shortDescription = "Issue summary is required.";
  } else if (draft.shortDescription.trim().length > MAX_SHORT_DESCRIPTION_LENGTH) {
    errors.shortDescription = `Issue summary must be ${MAX_SHORT_DESCRIPTION_LENGTH} characters or fewer.`;
  }

  if (!draft.description.trim()) {
    errors.description = "Issue description is required.";
  } else if (draft.description.trim().length > MAX_DESCRIPTION_LENGTH) {
    errors.description = `Issue description must be ${MAX_DESCRIPTION_LENGTH} characters or fewer.`;
  }

  return errors;
};

const ServiceNowIncidentModal: React.FC<Props> = ({ visible, ticketId, existingIncidentNumber, onClose, onProceed, isSubmitting = false }) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [draft, setDraft] = useState<ServiceNowIncidentDraft | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ServiceNowIncidentValidationErrors>({});

  useEffect(() => {
    let isMounted = true;

    const loadPreview = async () => {
      if (!visible || !ticketId) {
        return;
      }

      setIsLoading(true);
      setLoadError(null);
      setValidationErrors({});

      try {
        const api = createApiClient();
        const response = await api.get<ServiceNowIncidentPreview>(
          `/servicenow/preview?ticketId=${encodeURIComponent(ticketId)}`
        );

        if (isMounted) {
          setDraft(toDraft(response.data, existingIncidentNumber));
        }
      } catch (error: any) {
        if (isMounted) {
          setLoadError(error?.response?.data?.message || error?.message || "Unable to load ServiceNow incident preview.");
          setDraft(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadPreview();

    return () => {
      isMounted = false;
    };
  }, [ticketId, visible]);

  const description = useMemo(() => {
    if (loadError) {
      return "Preview data could not be loaded. Close this dialog and try again.";
    }

    return "Review the ServiceNow incident draft, update any missing details, or link an existing incident before proceeding.";
  }, [loadError]);

  const updateDraftField = <K extends keyof ServiceNowIncidentDraft>(field: K, value: ServiceNowIncidentDraft[K]) => {
    setDraft((current) => (current ? { ...current, [field]: value } : current));
    if (field === "shortDescription" || field === "description") {
      setValidationErrors((current) => ({ ...current, [field]: undefined }));
    }
  };

  const handleProceed = async () => {
    if (!draft) {
      return;
    }

    const errors = validateDraft(draft);
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    await onProceed(draft);
  };

  const renderReadOnlyField = (label: string, value?: string) => (
    <View style={styles.fieldBlock}>
      <Text style={[styles.label, isDark && { color: "#B0B0B0" }]}>{label}</Text>
      <View style={[styles.readOnlyField, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040" }]}>
        <Text style={[styles.readOnlyValue, isDark && { color: "#F2F2F2" }]}>{value?.trim() || "Not provided"}</Text>
      </View>
    </View>
  );

  const renderInputField = ({
    label,
    value,
    onChangeText,
    multiline = false,
    placeholder,
    error,
    helpText,
  }: {
    label: string;
    value: string;
    onChangeText: (text: string) => void;
    multiline?: boolean;
    placeholder?: string;
    error?: string;
    helpText?: string;
  }) => (
    <View style={styles.fieldBlock}>
      <Text style={[styles.label, isDark && { color: "#B0B0B0" }]}>{label}</Text>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={isDark ? "#6E6E6E" : "#999999"}
        multiline={multiline}
        numberOfLines={multiline ? 5 : 1}
        textAlignVertical={multiline ? "top" : "center"}
        style={[
          multiline ? styles.textArea : styles.textInput,
          isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040", color: "#F2F2F2" },
          error ? styles.textInputError : null,
        ]}
      />
      {error ? <Text style={styles.errorText}>{error}</Text> : null}
      {helpText ? <Text style={[styles.helpText, isDark && { color: "#6E6E6E" }]}>{helpText}</Text> : null}
    </View>
  );

  return (
    <LHModalPop
      visible={visible}
      onClose={onClose}
      title="Create ServiceNow Incident"
      description={description}
      showFooter={true}
      primaryButtonText="Proceed with Incident Creation"
      secondaryButtonText="Cancel"
      onPrimaryPress={handleProceed}
      onSecondaryPress={onClose}
      showCloseButton={true}
      primaryButtonLoading={isLoading || isSubmitting}
      primaryButtonDisabled={isLoading || isSubmitting || !draft}
      enableScroll={true}
      width={Platform.OS === "web" ? 720 : "92%"}
    >
      {isLoading ? (
        <View style={styles.loadingState}>
          <ActivityIndicator size="small" color={isDark ? "#3D8BFF" : "#005CBC"} />
          <Text style={[styles.loadingText, isDark && { color: "#B0B0B0" }]}>Loading incident preview…</Text>
        </View>
      ) : null}

      {!isLoading && loadError ? (
        <View style={[styles.errorState, isDark && { backgroundColor: "#2B0D00", borderColor: "#5C1A00" }]}>
          <Text style={styles.errorTitle}>Unable to load preview</Text>
          <Text style={styles.errorBody}>{loadError}</Text>
          <Pressable onPress={onClose} style={({ pressed }) => [styles.inlineCloseButton, pressed && styles.inlineCloseButtonPressed]}>
            <Text style={styles.inlineCloseButtonText}>Close</Text>
          </Pressable>
        </View>
      ) : null}

      {!isLoading && draft ? (
        <View style={styles.form}>
          <View style={styles.gridRow}>
            <View style={styles.gridColumn}>{renderReadOnlyField("Navi Ticket ID", draft.ntiId)}</View>
            <View style={styles.gridColumn}>{renderReadOnlyField("Submitter", draft.submitterEmail)}</View>
          </View>

          <View style={styles.gridRow}>
            <View style={styles.gridColumn}>{renderReadOnlyField("Urgency", draft.urgency)}</View>
          </View>

          {renderInputField({
            label: "Issue Summary",
            value: draft.shortDescription,
            onChangeText: (value) => updateDraftField("shortDescription", value),
            error: validationErrors.shortDescription,
            helpText: `Prefilled from the ticket title. Maximum ${MAX_SHORT_DESCRIPTION_LENGTH} characters.`,
          })}

          {renderInputField({
            label: "Issue Description",
            value: draft.description,
            onChangeText: (value) => updateDraftField("description", value),
            multiline: true,
            error: validationErrors.description,
            helpText: `Prefilled from the ticket details. Maximum ${MAX_DESCRIPTION_LENGTH} characters.`,
          })}

          <View style={styles.gridRow}>
            <View style={styles.gridColumn}>
              {renderInputField({
                label: "Location / Market / Region",
                value: draft.location,
                onChangeText: (value) => updateDraftField("location", value),
              })}
            </View>
          </View>

          <View style={styles.gridRow}>
            <View style={styles.gridColumn}>
              {renderInputField({
                label: "SAP ID",
                value: draft.sapId,
                onChangeText: (value) => updateDraftField("sapId", value),
                helpText: "Current value is based on existing ticket fields and may need final IT mapping confirmation.",
              })}
            </View>
          </View>

          {renderInputField({
            label: "Link to Existing Incident (Optional)",
            value: draft.existingIncidentNumber,
            onChangeText: (value) => updateDraftField("existingIncidentNumber", value),
            placeholder: "INC0012345",
            helpText: "If provided, the next backend step should attach this Navi ticket to the existing ServiceNow incident instead of creating a new one.",
          })}
        </View>
      ) : null}
    </LHModalPop>
  );
};

const styles = StyleSheet.create({
  loadingState: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 32,
    gap: 12,
  },
  loadingText: {
    fontSize: 13,
    color: "#4B5563",
  },
  errorState: {
    borderWidth: 1,
    borderColor: "#F3C7BF",
    backgroundColor: "#FFF5F3",
    borderRadius: 10,
    padding: 16,
    gap: 8,
  },
  errorTitle: {
    fontSize: 14,
    fontWeight: "600",
    color: "#8A2200",
  },
  errorBody: {
    fontSize: 13,
    lineHeight: 19,
    color: "#8A2200",
  },
  inlineCloseButton: {
    alignSelf: "flex-start",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: "#FDE7E1",
  },
  inlineCloseButtonPressed: {
    opacity: 0.8,
  },
  inlineCloseButtonText: {
    color: "#8A2200",
    fontSize: 13,
    fontWeight: "600",
  },
  form: {
    gap: 16,
    paddingBottom: 8,
  },
  gridRow: {
    flexDirection: Platform.OS === "web" ? "row" : "column",
    gap: 16,
  },
  gridColumn: {
    flex: 1,
  },
  fieldBlock: {
    gap: 6,
  },
  label: {
    fontSize: 12,
    fontWeight: "700",
    color: "#374151",
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  readOnlyField: {
    minHeight: 42,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 11,
    backgroundColor: "#F9FAFB",
  },
  readOnlyValue: {
    fontSize: 13,
    color: "#2D2D2D",
  },
  textInput: {
    minHeight: 42,
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 9,
    fontSize: 13,
    color: "#2D2D2D",
    backgroundColor: "#FAFAFA",
  },
  textArea: {
    minHeight: 116,
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 13,
    color: "#2D2D2D",
    backgroundColor: "#FAFAFA",
  },
  textInputError: {
    borderColor: "#C12C01",
    backgroundColor: "#FFF5F3",
  },
  errorText: {
    fontSize: 12,
    color: "#C12C01",
  },
  helpText: {
    fontSize: 11,
    color: "#6B7280",
    lineHeight: 16,
  },
});

export default ServiceNowIncidentModal;